/** @jsx isolateComponent */

import { MouseEvent } from 'react';

import { isolateComponent } from '@zapier/common-utils';
import { Button, Colors, DefaultButtonColors } from '@zapier/design-system';

import { PlanV2 } from '@zapier/design-system-revenue/src/types';

import {
  getChangePlanType,
  isFree,
} from '@zapier/design-system-revenue/src/utils';

import { getButtonColor, getChangePlanLabel, isDisabled } from './utils';

type PlanActionButtonProps = {
  isUserOnLegacyPlan: boolean;
  onClick: (...args: any[]) => void;
  /** The plan associated with the plan card that this button is in. */
  planCardPlan: PlanV2;
  /** The user's current plan. Will be undefined if the user is logged out. */
  userPlan?: PlanV2;
};

const customGreyTheme = {
  // Removes hover and changes focus for grey buttons
  custom: {
    base: {
      backgroundColor: Colors.grey100,
      color: Colors.blue500,
    },
    disabled: {
      backgroundColor: Colors.grey100,
      color: Colors.grey500,
    },
    hover: {
      backgroundColor: Colors.grey200,
      color: Colors.blue500,
    },
    focus: {
      backgroundColor: Colors.grey100,
      outlineColor: Colors.blue500,
    },
  },
  ...DefaultButtonColors,
};

/**
 * A button component used to trigger an action (like purchase) for a specified plan.
 */
export const PlanActionButton = ({
  isUserOnLegacyPlan,
  onClick,
  planCardPlan,
  userPlan,
}: PlanActionButtonProps) => {
  const isLoggedOutPricingPage = !userPlan;
  const isPlanCardPlanFree = isFree(planCardPlan);

  const changePlanType = getChangePlanType({
    fromPlan: userPlan,
    toPlan: planCardPlan,
  });

  const color = getButtonColor({
    changeType: changePlanType,
    isLoggedOutPricingPage,
    isUserOnLegacyPlan,
  });

  const isCustomColorTheme = color === 'custom';

  return planCardPlan ? (
    <Button
      color={color}
      colorTheme={customGreyTheme}
      disabled={isDisabled({
        isPlanCardPlanFree,
        isUserOnLegacyPlan,
        isUserPlanFree: isFree(userPlan),
      })}
      hasShadow={!isCustomColorTheme}
      isFullWidth={true}
      onClick={(e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onClick(planCardPlan, changePlanType);
      }}
    >
      {getChangePlanLabel({
        changeType: changePlanType,
        isLoggedOutPricingPage,
        isPlanCardPlanFree,
        isUserOnLegacyPlan,
      })}
    </Button>
  ) : null;
};
